import { defineComponent as _defineComponent } from 'vue'
import DialogManager from '@/components/layout/dialogs/DialogManager.vue';

import Header from '@/components/layout/header/Header.vue';
import Sidebar from '@/components/layout/sidebars/Sidebar.vue';
import SidebarManager from '@/components/layout/sidebars/SidebarManager.vue';

import { SIDEBAR_ARTICLE_HEIGHT, SIDEBAR_HEADER_HEIGHT } from '@/constants/SidebarConsts';
import EventBus from '@/EventBus';
import { APP_REFRESH, SCROLL_TO_ARTICLE } from '@/events';
import helper from '@/helper';
import { User } from '@/models/User';
import { WorkspaceVisibility } from '@/other/WorkspaceVisibility';
import * as routerNames from '@/routerNames';
import { ADMIN_PANEL, BROWSE, BROWSE_PARAGRAPH, BROWSE_SHARED, BROWSE_TEXT_TAB } from '@/routerNames';
import authService from '@/services/AuthService';
import mobileResponsiveService from '@/services/MobileResponsiveService';
import workspaceService from '@/services/workspace/WorkspaceService';
import store from '@/store';
import { commitSetArticleEditorArticleHash } from '@/store/commits/articleEditorCommits';
import { commitSetSidebarWidth, commitToggleMainSidebar } from '@/store/commits/uiCommits';
import { dispatchInitializeApp } from '@/store/dispatchers/appDispatchers';
import { isSnaploreClosedFor } from '@/helpers/appContext';
import { Mutations } from '@/store/mutations';
import { AppStateState } from '@/store/state/AppState';
import { debounce, DebouncedFunc } from 'lodash';
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import { CompanyRole } from './@types/CompanyRole';
import { CompanyUserStatus } from './@types/enums/CompanyUserStatus';
import globalEventHandler from './handlers/GlobalEventHandler';
import { Company } from './models/Company';


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const router = useRouter();
const route = useRoute();

const topWithBanner = ref(40);
/* #region sidebar resizing related data and methods */
const isSidebarResizing = ref(false);
const currentSidebarWidth = ref(170);
const initSidebarWidth = ref(170);
const maxResizeWidth = ref(600);
const mainContentPadding = ref(0);
const storedMainContentPadding = ref<number | null>(null);
const dragAreaHeight = ref(100);

const sidebarTimeout = ref<number>();
const updateSidebarWidth = ref<DebouncedFunc<() => void>>();

const draggingSidebarBlock = ref<HTMLDivElement>();
const appContent = ref<HTMLDivElement>();
const appElement = ref<HTMLDivElement>();
const sidebarScroll = ref();

const state = computed((): AppStateState => store.state.state);

const routeKey = computed((): string => {
  const name = route.name;

  switch (name) {
    case routerNames.BROWSE:
    case routerNames.BROWSE_TEXT_TAB:
    case routerNames.BROWSE_PARAGRAPH:
      return 'browse';
    default:
      return name;
  }
});

const isCompanyPaid = computed(() => store.state.company?.isPaid);

const isSuperAdmin = computed(() => user.value?.isSuperAdmin());

// SNAP-2241: the main sidebar is all Snaplore and loads its counts from routes closed to such a company.
const isSnaploreClosed = computed(() => isSnaploreClosedFor(store.state.company, user.value));

const isSettingsPage = computed(() => route.path.indexOf('settings') > 0);

const isShowDeactivatedPage = computed(() => isCompanyDeactivated.value && !isAuthPage.value && !isSettingsPage.value);

const isShowUserDeactivatedPage = computed(
  () => isUserInactiveInCompany.value && !isAuthPage.value && !isSettingsPage.value
);

const isMainSidebarExpanded = computed((): boolean => store.state.ui.isMainSidebarExpanded);

const isMainSidebar = computed((): boolean => store.state.ui.isMainSidebar);

const user = computed((): User => store.state.user);

const company = computed((): Company => store.state.company);

const isSharedMode = computed((): boolean => {
  const currentRoute = route.name;
  const isSharedPage = currentRoute === routerNames.BROWSE_SHARED;
  const isSharedPublic =
    !user.value && workspaceService?.workspaceView?.workspace?.visibility === WorkspaceVisibility.public;

  return isSharedPage || isSharedPublic;
});

const isAuthPage = computed((): boolean => {
  const currentRoute = route.name;

  return (
    currentRoute === routerNames.SIGNUP ||
    currentRoute === routerNames.SIGNUP_INVITED ||
    currentRoute === routerNames.LOGIN_SOCIAL_CALLBACK ||
    currentRoute === routerNames.LOGIN_SOCIAL_FAIL ||
    currentRoute === routerNames.LOGIN_SOCIAL_TWO_FACTOR ||
    currentRoute === routerNames.LOGIN ||
    currentRoute === routerNames.PASSWORD_RESET ||
    currentRoute === routerNames.PASSWORD_RESET_TOKEN ||
    currentRoute === routerNames.ACCOUNT_RECOVERY_REDEEM ||
    currentRoute === routerNames.ACCOUNT_RECOVERY_CANCEL ||
    currentRoute === routerNames.OAUTH_CONSENT
  );
});

const isSidebar = computed((): boolean => {
  const currentRouteName = route.name;

  return (
    [
      routerNames.BROWSE,
      routerNames.BROWSE_TEXT_TAB,
      routerNames.BROWSE_PARAGRAPH,
      routerNames.BROWSE_SHARED,
      routerNames.ARTICLE_EDIT,
      routerNames.ARTICLE_CREATE,
      routerNames.DRAFT_EDIT,
      routerNames.ARTICLE_VERSION_EDIT,
      routerNames.ADMIN_PANEL,
    ].includes(currentRouteName) || isSettingsPage.value
  );
});

const isAuthorized = computed(() => store.state.user && authService.isAuthorized());

const isUserInactiveInCompany = computed((): boolean => store.state.isUserInactiveInCompany);

const isCompanyDeactivated = computed((): boolean => company.value && !company.value.isActivated);

const isMobile = computed((): boolean => mobileResponsiveService.isMobileView);

const isSidebarOpen = computed((): boolean => currentSidebarWidth.value !== 0);

const isEditPage = computed((): boolean => {
  const currentRoute = route.name;

  return (
    currentRoute === routerNames.ARTICLE_EDIT ||
    currentRoute === routerNames.ARTICLE_CREATE ||
    currentRoute === routerNames.DRAFT_EDIT ||
    currentRoute === routerNames.ARTICLE_VERSION_EDIT
  );
});

const isBillingPage = computed((): boolean => {
  const currentRoute = route.name;

  return currentRoute === routerNames.BILLING_PLANS || currentRoute === routerNames.CHECKOUT;
});

const appContainerClasses = computed(() => {
  const isWorkspacePage = ['Workspaces', 'WorkspaceEdit'].includes(route.name);
  const isBrowsePage = [BROWSE, BROWSE_PARAGRAPH, BROWSE_SHARED, BROWSE_TEXT_TAB].includes(route.name);

  const isCompanyAdminPage = route.name === ADMIN_PANEL && route.params.page === 'company';

  return {
    'no-padding': ((!isCompanyPaid.value && !isSuperAdmin.value) || isBillingPage.value) && !isBrowsePage,
    'edit-article-container': !isAuthPage.value && isEditPage.value,
    'no-sidebar':
      (!isAuthPage.value && !isEditPage.value && (isCompanyPaid.value || isSuperAdmin.value)) || !isBillingPage.value,
    'py-xl-0 py-lg-0 py-md-0 py-sm-4 py-5 md-layout md-aligment-center-center': isAuthPage.value,
    'workspace-content': isWorkspacePage && !isAuthPage.value,
    'no-side-padding': isCompanyAdminPage,
    content: !isWorkspacePage && !isAuthPage.value,
  };
});

const paddingLeft = computed(() => {
  const basePadding = mainContentPadding.value;

  const shouldAddSidebarPadding = !isAuthPage.value && isAuthorized.value && isMainSidebar.value && !isMobile.value;

  if (!shouldAddSidebarPadding) {
    return `${basePadding}px`;
  }

  const sidebarWidth =
    isBillingPage.value || isSnaploreClosed.value
      ? 0
      : isCompanyPaid.value || isSuperAdmin.value
      ? isMainSidebarExpanded.value
        ? 170
        : isEditPage.value
        ? 56
        : 60
      : 0;

  return `${basePadding + sidebarWidth}px`;
});

const sidebarStyles = computed(() => ({
  width: isMobile.value && isMainSidebar.value && !isAuthorized.value ? '345px' : `${currentSidebarWidth.value}px`,
  left: !isAuthorized.value || isSnaploreClosed.value ? '0' : isMainSidebarExpanded.value ? '170px' : '60px',
  transition: 'left 0.05s ease-in-out',
  paddingTop: appTopStyle.value,
}));

const appContentStyles = computed(() => ({
  paddingLeft: paddingLeft.value,
  transition: isSidebarResizing.value ? 'none' : 'padding 0.05s ease',
}));

const appTopStyle = computed((): string =>
  isMobile.value || isAuthPage.value
    ? '0px'
    : (store.state.ui.isExtensionBannerVisible && (isCompanyPaid.value || isSuperAdmin.value) && !isBillingPage.value
        ? topWithBanner.value
        : 0) + 'px'
);

const appWrapperStyle = () => {
  return {
    backgroundColor: isBillingPage.value ? '#F7F9FF' : '',
  };
};

const handleMousedown = () => (isSidebarResizing.value = true);

const showResizeBorder = (e: MouseEvent) => {
  const headerHeight = 60;
  const draggingBlock = draggingSidebarBlock.value;

  if (
    draggingBlock &&
    !!draggingBlock.getBoundingClientRect() &&
    draggingBlock.getBoundingClientRect().x - e.clientX >= -15 &&
    draggingBlock.getBoundingClientRect().x - e.clientX <= 0 &&
    e.clientY > headerHeight
  ) {
    document.body.style.cursor = 'ew-resize';
    draggingBlock.style.borderRight = '1px solid #7D97F4';
  } else if (draggingBlock?.style) {
    draggingBlock.style.borderRight = 'none';
    document.body.style.cursor = 'auto';
  }
};

const handleMousemove = (e: MouseEvent) => {
  showResizeBorder(e);

  // ignore if don't resizing.
  if (!isSidebarResizing.value) {
    return;
  }

  const offsetLeft = e.clientX - document.body.offsetLeft - 170;

  if (offsetLeft >= initSidebarWidth.value && offsetLeft <= maxResizeWidth.value) {
    currentSidebarWidth.value = offsetLeft;
    mainContentPadding.value =
      currentSidebarWidth.value - initSidebarWidth.value >= 0 ? currentSidebarWidth.value - 340 : 0;
    updateSidebarWidth.value();
  }
};

const onCloseSidebarMobile = (mainContainer?: any) => {
  if (mainContainer) {
    const mainTag = mainContainer[0] || mainContainer;
    mainTag.style.paddingLeft = '0px';
  }

  if (isMainSidebar.value) {
    mainContentPadding.value = 0;
    return;
  }

  mainContentPadding.value = 0;
  currentSidebarWidth.value = 0;
};

const handleMouseup = () => {
  if (isSidebarResizing.value === true) {
    // EventBus.$emit('side-bars-width-changed', currentSidebarWidth.value);
  }

  isSidebarResizing.value = false;
};

const scrollToFocusedArticle = () => {
  const selectedArticleInDom: any = appElement.value?.querySelector('.active.article-item-container');

  if (selectedArticleInDom) {
    const foundParentOffsetTop = helper.searchForSelectedArticleParent(selectedArticleInDom);
    const resultOffsetTop = foundParentOffsetTop + SIDEBAR_ARTICLE_HEIGHT + SIDEBAR_HEADER_HEIGHT;

    sidebarScroll.value.$el.scrollTo(0, resultOffsetTop);
  }
};

// making sidebar resizable brokest styles from vue-material
// need to fix some style from code
const changeMainBlockPadding = (routeName: string) => {
  const mainContainer: any = document.getElementsByClassName('md-app-container');

  if (!!mainContainer && !mainContainer.length) {
    //setTimeout because vue-material can rewrite the styles or doesn't upload the tag
    sidebarTimeout.value = window.setTimeout(() => {
      changeMainBlockPadding(routeName);
      clearTimeout(sidebarTimeout.value);
    }, 0);

    return;
  }

  if (isMobile.value) {
    setTimeout(() => {
      onCloseSidebarMobile(mainContainer);
    }, 300);

    return;
  }

  switch (routeName) {
    case routerNames.DRAFTS: {
      // add here routes without sidebar
      storedMainContentPadding.value = mainContentPadding.value;
      // clean padding-left for main content
      mainContentPadding.value = 0;
      //set the default width for workspase sidebar
      EventBus.$emit('side-bars-width-changed', initSidebarWidth.value);

      break;
    }
    case routerNames.LOGIN: {
      if (mainContainer && mainContainer.length) {
        mainContainer[0].style.paddingLeft = '0';
        setTimeout(() => {
          mainContainer[0].style.paddingLeft = '0';
        }, 0);
      }

      break;
    }
    // any route with sidebar
    case routerNames.BROWSE:
    case routerNames.BROWSE_TEXT_TAB:
    case routerNames.BROWSE_PARAGRAPH: {
      if (route.params.articleSlug) {
        EventBus.$on(SCROLL_TO_ARTICLE, scrollToFocusedArticle);
      } else {
        EventBus.$off(SCROLL_TO_ARTICLE, scrollToFocusedArticle);
      }
      break;
    }
    case routerNames.BROWSE_SHARED:
    case routerNames.ARTICLE_EDIT:
    case routerNames.ARTICLE_CREATE:
    case routerNames.ARTICLE_VERSION_EDIT:
    case routerNames.DRAFT_EDIT:
    case routerNames.SETTINGS:
    case routerNames.COMPANY:
    case routerNames.COMPANY_GROUPS:
    case routerNames.USER_PROFILE:
    case routerNames.ACTIVE_SESSIONS:
    case routerNames.USER_MANAGEMENT:
    case routerNames.BILLING_PLAN_DETAILS:
    case routerNames.BILLING_HISTORY:
    case routerNames.ADMIN_PANEL: {
      // set default padding for main container
      // need these ugly timeouts cause vue-material could rewrite it
      if (mainContainer && mainContainer.length) {
        mainContainer[0].style.paddingLeft = '345px';
        setTimeout(() => {
          mainContainer[0].style.paddingLeft = '345px';
        }, 0);
      }
      // if wee have some stored sidebar width => restore it.
      if (storedMainContentPadding.value != null) {
        mainContentPadding.value = storedMainContentPadding.value;
        storedMainContentPadding.value = null;
      }
      // inform workplace sidebar about width changes
      EventBus.$emit('side-bars-width-changed', currentSidebarWidth.value);
      break;
    }
    default: {
      if (storedMainContentPadding.value != null) {
        mainContentPadding.value = storedMainContentPadding.value;
        storedMainContentPadding.value = null;
      }

      // inform workspaces sidebar about change width of main sidebar
      EventBus.$emit('side-bars-width-changed', currentSidebarWidth.value);
    }
  }
};

/* #endregion */

const checkUserStatusInCompany = () => {
  if (!company.value) {
    return store.commit(Mutations.SET_USER_INACTIVE_IN_COMPANY, false);
  }

  const roles: CompanyRole[] = company.value.roles;
  const deactivated = roles?.filter(
    (item: CompanyRole) => item.status === CompanyUserStatus.DEACTIVATED && item.user === user.value.id
  );

  if (deactivated && deactivated.length) {
    if (user.value.companyIds.length) {
      authService.endSession().finally(() => router.push({ name: routerNames.LOGIN }));
    }
    return store.commit(Mutations.SET_USER_INACTIVE_IN_COMPANY, true);
  }
  return store.commit(Mutations.SET_USER_INACTIVE_IN_COMPANY, false);
};

const handleRefresh = () => {
  store.commit('setAppState', 'refreshing');
  setTimeout(() => store.commit('setAppState', 'ready'), 100);
};

const setDefaultSizes = (mainContainer: any) => {
  if (mainContainer && isSidebar.value) {
    const mainTag = mainContainer[0] || mainContainer;

    mainTag.style.paddingLeft = '345px';
    currentSidebarWidth.value = 345;
  }
};

const handleDraggableAreaHeight = (minHeight: number) => (dragAreaHeight.value = minHeight);
const handleWindowResize = () => mobileResponsiveService.setMobileViewProperties();

const onToggleSidebarMobile = () => {
  const mainTag = appContent.value && appContent.value.parentElement?.parentElement;

  if (isMobile.value) {
    if (currentSidebarWidth.value === 0) {
      currentSidebarWidth.value = 345;
      mainContentPadding.value = 0;
    }
  } else {
    setDefaultSizes(mainTag);
  }
};

watch(
  () => route,
  () => {
    checkUserStatusInCompany();
    onToggleSidebarMobile();

    helper.checkWindowTheme();

    if (isShowDeactivatedPage.value && !isSuperAdmin.value) {
      router.push({ name: routerNames.INACTIVE_COMPANY });
    }

    if (isShowUserDeactivatedPage.value && !isSuperAdmin.value) {
      router.push({ name: routerNames.USER_DEACTIVATED });
    }

    commitSetArticleEditorArticleHash(null);

    changeMainBlockPadding(route.name);
    dragAreaHeight.value = 100; // reset drag area height for prevent breaking markups
  },
  {
    immediate: true,
    deep: true,
  }
);

watch(isMobile, () => {
  if (isMobile.value) {
    onToggleSidebarMobile();
  }

  if ((isMobile.value && isMainSidebar.value) || (!isMobile.value && !isMainSidebar.value)) {
    commitToggleMainSidebar();
  }
});

watch(isMainSidebar, () => {
  if (isMainSidebar.value && isMobile.value) {
    onToggleSidebarMobile();
  }
});

onBeforeMount(async () => {
  currentSidebarWidth.value = isMainSidebar.value ? 340 : 170;
  updateSidebarWidth.value = debounce(() => {
    commitSetSidebarWidth(currentSidebarWidth.value);
  }, 200) as DebouncedFunc<() => void>;

  globalEventHandler.setApp(this);
  globalEventHandler.register(EventBus);

  handleWindowResize();

  EventBus.$on(APP_REFRESH, handleRefresh);
  EventBus.$on('toggle-mobile-sidebar', onToggleSidebarMobile);
  EventBus.$on('close-mobile-sidebar', onCloseSidebarMobile);
  EventBus.$on('min-draggable-area-height-changed', handleDraggableAreaHeight);
  document.addEventListener('mousemove', handleMousemove);
  document.addEventListener('mouseup', handleMouseup);
  window.addEventListener('resize', handleWindowResize);

  dispatchInitializeApp();
});

onBeforeUnmount(() => {
  EventBus.$off(APP_REFRESH, handleRefresh);
  EventBus.$off('min-draggable-area-height-changed', handleDraggableAreaHeight);
  EventBus.$off(SCROLL_TO_ARTICLE, scrollToFocusedArticle);
  EventBus.$off('toggle-mobile-sidebar', onToggleSidebarMobile);
  EventBus.$off('close-mobile-sidebar', onCloseSidebarMobile);

  document.removeEventListener('mousemove', (e) => handleMousemove(e));
  document.removeEventListener('mouseup', handleMouseup);
  window.removeEventListener('resize', handleWindowResize);
});

return { __sfc: true,router, route, topWithBanner, isSidebarResizing, currentSidebarWidth, initSidebarWidth, maxResizeWidth, mainContentPadding, storedMainContentPadding, dragAreaHeight, sidebarTimeout, updateSidebarWidth, draggingSidebarBlock, appContent, appElement, sidebarScroll, state, routeKey, isCompanyPaid, isSuperAdmin, isSnaploreClosed, isSettingsPage, isShowDeactivatedPage, isShowUserDeactivatedPage, isMainSidebarExpanded, isMainSidebar, user, company, isSharedMode, isAuthPage, isSidebar, isAuthorized, isUserInactiveInCompany, isCompanyDeactivated, isMobile, isSidebarOpen, isEditPage, isBillingPage, appContainerClasses, paddingLeft, sidebarStyles, appContentStyles, appTopStyle, appWrapperStyle, handleMousedown, showResizeBorder, handleMousemove, onCloseSidebarMobile, handleMouseup, scrollToFocusedArticle, changeMainBlockPadding, checkUserStatusInCompany, handleRefresh, setDefaultSizes, handleDraggableAreaHeight, handleWindowResize, onToggleSidebarMobile, DialogManager, Header, Sidebar, SidebarManager }
}

})